import { Facebook, Instagram, Music2 } from "lucide-react";
import { RegisterForm } from "@/components/teaser/RegisterForm";
import { media } from "@/lib/api";

export const Hero = ({ c = {}, config, onRegistered }) => (
  <section className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section" aria-labelledby="hero-title">
    <img src={media(c.image)} alt="" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-[#0D0C09]/70" />
    <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#131413] to-transparent" />
    <div className="relative mx-auto flex min-h-[100svh] max-w-[1440px] flex-col items-center justify-center px-5 py-16 text-center sm:px-20">
      <img src={media(config?.page?.brand?.logo) || "/img/logo-wf.png"} alt="The Murdér Express" width="557" height="272" className="h-auto w-[180px] sm:w-[240px] lg:w-[280px]" data-testid="hero-logo" />
      <div className="flex max-w-4xl flex-col items-center pt-10 md:pt-14">
        <h1 id="hero-title" className="font-head text-[1.85rem] sm:text-5xl lg:text-[4.2rem]">
          <span className="line-mask"><span style={{ "--d": "0.15s" }}>{c.heading}</span></span>
        </h1>
        <p className="fade-up font-body mt-6 flex flex-col items-center gap-2 text-lg font-medium sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3 sm:text-xl lg:w-max lg:flex-nowrap lg:whitespace-nowrap" style={{ "--d": "0.5s" }} data-testid="hero-meta-line">
          {[[c.show_name, "uppercase tracking-[0.12em]", "hero-show-name"], [c.date_line, "", "hero-date"], [c.location, "uppercase tracking-[0.12em]", "hero-location"]].filter(([t]) => t).map(([t, cls, id], i) => (
            <span key={id} className="flex items-center gap-3">
              {i > 0 && <span className="hidden h-5 w-px bg-[#F2EBDD]/50 sm:block" aria-hidden="true" />}
              <span className={cls} data-testid={id}>{t}</span>
            </span>
          ))}
        </p>
        <p className="fade-up font-body mt-6 text-[15px] leading-[1.5] text-[#F2EBDD]/90 md:text-[18px]" style={{ "--d": "0.6s" }}>{c.text}</p>
        <div className="fade-up mt-4 flex w-full justify-center text-left" style={{ "--d": "0.7s" }}>
          <RegisterForm config={config} button={c.button} source="hero" idPrefix="hero" onRegistered={onRegistered} />
        </div>
      </div>
    </div>
  </section>
);

export const FinalCta = ({ c = {}, config, onRegistered }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-16 sm:px-20" data-testid="final-section">
    <div className="border border-[#F2EBDD]/20 p-6 sm:p-12">
      <h2 className="font-head max-w-5xl text-2xl sm:text-3xl lg:text-[56px]">{c.heading}</h2>
      <p className="font-body mt-6 text-[15px] leading-[1.5] text-[#F2EBDD]/85 md:text-[18px]">{c.text}</p>
      <div className="mt-4"><RegisterForm config={config} button={c.button} source="final" idPrefix="final" onRegistered={onRegistered} /></div>
    </div>
  </section>
);

const SOCIAL = [["instagram", "Instagram", Instagram], ["tiktok", "TikTok", Music2], ["facebook", "Facebook", Facebook]];

const hasText = (t) => !!(t && t.trim() && !t.trim().startsWith("["));

export const Footer = ({ links = {}, credits = [], logo, privacyText, termsText, onCookies }) => {
  const a = "inline-flex min-h-[44px] items-center hover:text-[#C9A66B]";
  const socials = SOCIAL.filter(([k]) => links[k]);
  const privacy = links.privacy_url || (hasText(privacyText) ? "/privacy" : "");
  const terms = links.terms_url || (hasText(termsText) ? "/terms" : "");
  return (
    <footer className="mx-auto max-w-[1440px] px-5 pb-10 pt-10 sm:px-20" data-testid="site-footer">
      <div className="flex flex-col gap-6 border-b border-[#F2EBDD]/15 pb-6 md:flex-row md:items-center md:justify-between">
        <img src={media(logo) || "/img/logo-wf.png"} alt="The Murdér Express" width="557" height="272" loading="lazy" className="h-auto w-[120px]" data-testid="footer-logo" />
        <nav aria-label="Footer" className="font-body flex flex-wrap items-center gap-x-8 text-[13px] text-[#F2EBDD]/85">
          {links.contact_url && <a href={links.contact_url} className={a} data-testid="footer-contact-link">Contact</a>}
          {socials.length > 0 && <span className="flex gap-1">
            {socials.map(([k, label, Icon]) => (
              <a key={k} href={links[k]} target="_blank" rel="noreferrer" aria-label={label} className="flex h-11 w-11 items-center justify-center hover:text-[#C9A66B]" data-testid={`footer-social-${k}`}><Icon size={17} aria-hidden="true" /></a>
            ))}
          </span>}
          {privacy && <a href={privacy} className={a} data-testid="footer-privacy-link">Privacy Policy</a>}
          {terms && <a href={terms} className={a} data-testid="footer-terms-link">Offer terms</a>}
          <button type="button" onClick={onCookies} className={a} data-testid="footer-cookie-settings-button">Cookie settings</button>
        </nav>
      </div>
      <p className="font-body mt-5 text-[12px] text-[#F2EBDD]/70" data-testid="footer-credits">
        {(Array.isArray(credits) ? credits : []).filter((x) => x?.text).map((x, i) => (
          <span key={i}>{i > 0 && " · "}{x.url ? <a href={x.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#C9A66B]" data-testid={`footer-credit-link-${i}`}>{x.text}</a> : x.text}</span>
        ))}
      </p>
    </footer>
  );
};
